import { CloseOutlined, MoreOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Image,
  Menu,
  message,
  Modal,
  Pagination,
  Row,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import { DocumentsApiService } from 'features/document-report/api/documents-api.service';
import { useGetRequestDocumentList } from 'features/document-report/use-cases/get-request-documents';
import FileViewer from 'features/new-clients/components/clients-details/file-viewer';
import { useUserContext } from 'features/users';
import { Folder } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { formatUtcToLocal, getFileExtension } from 'routes/utils';

import { UploadRequestedDocumentsModal } from './upload-documents-requested';

export const RequestedDocumentView = ({
  setAddCommentsModal,
  setCommentsInfo,
  searchName,
  month,
  year,
  getDocumentsCount,
  finishedValue,
  initilaMarkFinished,
  requestdocuments,
  handleClick,
}: // requestdocuments
any) => {
  const [state, setState] = useState<any>(null);
  const [UploadModal, setUploadModal] = useState(false);
  const activetabValue: any = localStorage.getItem('activeTab');
  const [documentInfo, setDocumentsInfo] = useState('');
  const [loading, setLoading] = useState<boolean>(false); // Add loading state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // Number of items per page
  const [combinedData, setCombinedData] = useState<any[]>([]);

  const { user } = useUserContext();
  const params = {
    PageIndex: 1,
    RecordPerPage: 10,
  };
  const { data: pendingDocument, refetch: pendingrefetch } =
    useGetRequestDocumentList(
      {
        CompanyId: user?.CompanyId,
        PageIndex: currentPage,
        RecordPerPage: itemsPerPage,
        DocumentStatusId: 1,
        // PeriodId: selectedValue || 1,
        Year: year,
        Month: month,
        Name: searchName,
        ClientId: user?.Id,
      },
      { retry: false },
    );
  const intialFetch = async () => {
    setLoading(true); // Set loading to true before the API request
    try {
      const apiRes = await DocumentsApiService().getRequestDocument({
        CompanyId: user?.CompanyId,
        PageIndex: params.PageIndex,
        RecordPerPage: params.RecordPerPage,
        DocumentStatusId: 0, // for requested
        // PeriodId: selectedValue,
        Year: year,
        Month: month,
        Name: searchName,
        ClientId: user?.Id,
      });
      setState(apiRes);
    } catch (error) {
      console.error('Error fetching requested documents:', error);
    } finally {
      setLoading(false); // Set loading to false after the request completes
      pendingrefetch();
    }
  };
  const handleMenuClick = ({ key }: any, item: any) => {
    if (key === 'download') {
      message.success('Downloading ' + item.documentName);
      const link = document.createElement('a');
      link.href = item.documentPath; // File path or URL
      link.target = '_blank'; // Open in a new tab
      link.download = item.documentName; // Suggested file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (key === 'preview') {
      // Open the preview in a new tab
      Modal.info({
        title: 'Document View',
        width: '100%',
        icon: null,
        closable: true,
        closeIcon: <CloseOutlined />,
        style: { top: '0px', maxWidth: '500px' },
        content: (
          <div style={{ textAlign: 'start' }}>
            {(() => {
              const extension = getFileExtension(item.documentPath);

              if (['jpg', 'jpeg', 'png', 'gif'].includes(extension!)) {
                // If it's an image, display it directly
                return <Image src={item.documentPath} width='100%' />;
              } else if (
                [
                  'doc',
                  'docx',
                  'xls',
                  'xlsx',
                  'ppt',
                  'pptx',
                  'csv',
                  'pdf',
                ].includes(extension!)
              ) {
                // Use Google Docs Viewer for documents
                return (
                  <iframe
                    src={`https://docs.google.com/gview?url=${item.documentPath}&embedded=true`}
                    width='100%'
                    height='500'
                    style={{ border: 'none' }}
                    title='Document Preview'
                  />
                );
              } else {
                return (
                  <iframe
                    src={`${item.documentPath}`}
                    width='100%'
                    height='500'
                    style={{ border: 'none' }}
                    title='Document Preview'
                  />
                );
              }
            })()}
          </div>
        ),
        okButtonProps: { style: { display: 'none' } },
      });
    } else if (key === 'Add Comment') {
      setAddCommentsModal(true);
      setCommentsInfo(item);
    }
  };
  const renderMenu = (item: any) => (
    <Menu
      onClick={({ key }) => handleMenuClick({ key }, item)}
      items={[
        { label: 'Download', key: 'download' },
        { label: 'Preview', key: 'preview' },
        { label: 'Add Comment', key: 'Add Comment' },
      ]}
    />
  );
  const totalRecords = pendingDocument?.rc?.[0]?.totalRecords || 0;
  const paginatedData = pendingDocument?.rc;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  useEffect(() => {
    const combineLists = () => {
      const documentCheckArray = Array.isArray(requestdocuments?.rc)
        ? requestdocuments?.rc
        : [];
      const pendingDocumentArray = Array.isArray(state?.rc) ? state.rc : [];
      // Combine and deduplicate lists based on documentId
      const combinedMap = new Map();
      documentCheckArray.forEach((doc: any) =>
        combinedMap.set(doc.documentId, doc),
      );
      pendingDocumentArray.forEach((doc: any) =>
        combinedMap.set(doc.documentId, doc),
      );
      setCombinedData(Array.from(combinedMap.values()));
    };
    combineLists();
  }, [requestdocuments, state]);
  useEffect(() => {
    if (activetabValue == 2) {
      intialFetch();
      pendingrefetch();
      getDocumentsCount();
      initilaMarkFinished();
    }
  }, [activetabValue, searchName, year, month, currentPage]);

  return (
    <Fragment>
      <Row justify='start' align='middle' gutter={[16, 16]}>
        {loading ? (
          // Show loading spinner when fetching data
          <Col span={24} className='flex justify-center items-center'>
            <Spin size='large' />
          </Col>
        ) : requestdocuments?.rc && requestdocuments?.rc?.length > 0 ? (
          combinedData.map((item: any, index: number) => (
            <Col
              span={24}
              key={index}
              xxl={6}
              xl={8}
              lg={10}
              md={12}
              sm={24}
              role='button'
              className='self-stretch'
            >
              <Card
                className='mb-4 bg-[#F6F7F9] lgl_crd border-0'
                style={{ boxShadow: 'none' }}
                bordered={false}
                bodyStyle={{ padding: '16px' }}
              >
                <div className='flex gap-2'>
                  <Folder fill='#B2B7C8' stroke='none' size={62} />

                  <div className='flex flex-col grow'>
                    <span className='font-bold mb-1'>{item?.documentName}</span>
                    <div className='mb-4'>
                      <span
                        style={{ color: 'green' }}
                        className='bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded leading-5'
                      >
                        {item?.categoryName}
                      </span>{' '}
                      {/* Green color for categoryName */}
                      {item?.subCategoryName && (
                        <span
                          style={{ color: 'red' }}
                          className='bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded leading-5'
                        >
                          {item?.subCategoryName}
                        </span>
                      )}
                    </div>
                    {/* Red color for subCategoryName */}
                    {(!finishedValue?.rc ||
                      finishedValue?.rc.length === 0 ||
                      finishedValue?.rc[0]?.status === 1) && (
                      <Button
                        onClick={() => {
                          setUploadModal(true);
                          setDocumentsInfo(item);
                        }}
                        className='w-1/2'
                      >
                        Upload
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
          ))
        ) : (
          <Col span={24}>
            <Empty description='No Requested Documents Found' />
          </Col>
        )}
      </Row>

      {/* Pending Documents */}
      {/* paginatedData?.length */}
      {(paginatedData?.length as any) > 0 && (
        <div>
          <div className='text-lg font-semibold mb-2'>Pending Documents</div>
          {paginatedData?.length === 0 ? (
            <div className='text-gray-500'>
              <img src='./document_placeholder.png' />
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {paginatedData.map((item: any) => (
                <Col key={`pd${item.id}`} xxl={6} xl={8} lg={8} md={12} sm={24}>
                  <Card
                    role='button'
                    className='shadow-sm hover:bg-gray-50'
                    style={{ width: '100%', position: 'relative' }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <div className='flex gap-3'>
                      <div className='bg-white p-2 rounded-xl relative'>
                        <figure className='mediafolder'>
                          <FileViewer documentPath={item.documentPath} />
                        </figure>
                      </div>
                      {/* <figure className='mediafolder media'>
                        <FileViewer documentPath={item.documentPath} />
                      </figure> */}
                      <div className='flex flex-col grow'>
                        <div
                          className='flex justify-between '
                          style={{ alignItems: 'baseline' }}
                        >
                          <p className='font-bold'>{item?.documentName}</p>
                          <div className='flex align-middle'>
                            {item?.documentStatus && (
                              <Badge className='bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded leading-5'>
                                {item?.documentStatus}
                              </Badge>
                            )}
                            <Dropdown
                              overlay={renderMenu(item)}
                              trigger={['click']}
                            >
                              <MoreOutlined
                                style={{ fontSize: '24px', cursor: 'pointer' }}
                              />
                            </Dropdown>
                          </div>
                        </div>
                        <p className='text-xs text-gray-500'>
                          Last Updated{' '}
                          {/* {dayjs(item?.lastUpdatedDate).format('DD MMM YYYY')} */}
                          {formatUtcToLocal(
                            dayjs(item?.lastUpdatedDate).format('DD-MMM-YYYY'),
                          )}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      )}
      {/* Upload Modal */}
      {UploadModal && (
        <UploadRequestedDocumentsModal
          onClose={() => setUploadModal(false)}
          intialFetch={intialFetch}
          documentInfo={documentInfo}
          month={month}
          year={year}
          getDocumentsCount={getDocumentsCount}
          handleClick={handleClick}
        />
      )}
      {totalPages > 1 && (
        <Pagination
          current={currentPage}
          pageSize={itemsPerPage}
          total={totalRecords}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false} // Hides the page size dropdown
        />
      )}
    </Fragment>
  );
};
